import React, { useState, useEffect } from "react";
import swal from "sweetalert2";
import TutorFormStyle from "../../styles/TutorForm.module.css";
import { saveFormSizesData } from "../../services/api";
import Exporter from "./Exports/Exporter";
import MissingSizeAlert from './MissingSizeAlert';

const TutorForm = ({
  data,
  nicknameTooltipText,
  initialsTooltipText,
  onSuccessSubmit,
  missingSizesData,
  isMobileView
}) => {
  const [formRows, setFormRows] = useState([]);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [confirmState, setConfirmState] = useState("idle");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const rows = [];
    let idCounter = 0;

    data.customers.forEach((customer) => {
      (customer.items || []).forEach((item) => {
        for (let i = 0; i < item.pending_sizes; i++) {
          rows.push({
            id: idCounter++,
            customerId: customer.id,
            customerName: customer.name,
            itemId: item.id,
            itemName: item.name,
            alias: item.alias || "",
            size: "",
            nickname: "",
            initials: "",
            sizesAvailable: item["available_sizes"],
          });
        }
      });
    });

    setFormRows(rows);
  }, [data]);

  const handleRowChange = (id, field, value) => {
    const updated = formRows.map((row) => {
      if (row.id !== id) return row;

      if (field === "size") {
        const selectedSize = row.sizesAvailable.find((s) => s.code === value);
        return {
          ...row,
          size: value,
          width: selectedSize?.width || null,
          height: selectedSize?.height || null,
        };
      } else {
        return {
          ...row,
          [field]: value,
        };
      }
    });

    setFormRows(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const rowsToPreview = formRows
      .filter((row) => row.size !== "")
      .map((row) => ({
        name: row.customerName,
        itemName: row.itemName,
        nickname: row.nickname,
        initials: row.initials,
        size: row.size,
        width: row.width,
        height: row.height,
        order_item_id: row.itemId,
        customer_id: row.customerId,
      }));

    if (rowsToPreview.length === 0) {
      swal.fire({
        title: "Error",
        text: "Debe completar al menos un talle.",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#0071ce",
      });
      return;
    }

    setPreviewData(rowsToPreview);
    setIsPreviewing(true);
  };

  const confirmSubmit = async () => {
    setConfirmState("loading");

    const filteredData = previewData.map(
      ({ itemName, alias, ...rest }) => rest
    );

    try {
      const response = await saveFormSizesData(filteredData);
      console.log("Respuesta del servidor:", response);

      setConfirmState("success");

      setTimeout(() => {
        setIsPreviewing(false);
        setConfirmState("idle");
        swal.fire({
          title: "Éxito",
          text: "Los datos fueron enviados correctamente.",
          icon: "success",
          confirmButtonColor: "#0071ce",
        });

        if (typeof onSuccessSubmit === "function") {
          onSuccessSubmit();
        }
      }, 1500);
    } catch (error) {
      console.error("Error al enviar datos:", error);

      setConfirmState("idle");
      swal.fire({
        title: "Error",
        text: "Hubo un problema al enviar los datos. Por favor, intentá de nuevo.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  const goBackToForm = () => {
    setIsPreviewing(false);
  };

  // Orden del talle según la lista disponible (ya viene de menor a mayor). Las filas
  // sin talle cargado van al final.
  const sizeOrderIndex = (row) => {
    if (!row.size) return Number.POSITIVE_INFINITY;
    const idx = (row.sizesAvailable || []).findIndex((s) => s.code === row.size);
    return idx === -1 ? Number.POSITIVE_INFINITY : idx;
  };

  // Filas agrupadas POR TIPO DE PRENDA (para no mezclar artículos) y, dentro de cada
  // grupo, ordenadas por talle de menor a mayor a medida que se van cargando.
  const buildGroups = () => {
    const visibles = formRows.filter((row) =>
      row.customerName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const map = new Map();
    visibles.forEach((row) => {
      const key = `${row.itemName}__${row.alias}`;
      if (!map.has(key)) {
        map.set(key, { key, itemName: row.itemName, alias: row.alias, rows: [] });
      }
      map.get(key).rows.push(row);
    });
    const groups = [...map.values()];
    groups.forEach((g) =>
      g.rows.sort((a, b) => {
        const oa = sizeOrderIndex(a);
        const ob = sizeOrderIndex(b);
        if (oa !== ob) return oa - ob;
        return (a.customerName || "").localeCompare(b.customerName || "");
      })
    );
    return groups;
  };

  if (formRows.length === 0) {
    return (
      <div className={TutorFormStyle.emptyState}>
        <div className={TutorFormStyle.emptyStateContent}>
          <i className="bi bi-emoji-frown" style={{ fontSize: "2.5rem", color: "#0071ce" }}></i>
          <h3>No hay alumnos para cargar talles</h3>
          <p>Posibles razones:</p>
          <ul>
            <li>Todos los talles ya fueron cargados</li>
            <li>Ningún alumno seleccionó prendas</li>
          </ul>
        </div>
        <MissingSizeAlert />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={TutorFormStyle.form}>
      <MissingSizeAlert />
      {!isPreviewing && (
        <>
          <div className={TutorFormStyle.searchContainer}>
            <i className="bi bi-search" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={TutorFormStyle.searchInput}
            />
          </div>
            {isMobileView && (
              <div className={TutorFormStyle.mobileHelpSection}>
                <button 
                  type="button" 
                  onClick={nicknameTooltipText}
                  className={TutorFormStyle.mobileHelpButton}
                >
                  <i className="bi bi-question-circle"></i> Cómo usar el apodo
                </button>
                <button 
                  type="button" 
                  onClick={initialsTooltipText}
                  className={TutorFormStyle.mobileHelpButton}
                >
                  <i className="bi bi-question-circle"></i> Cómo usar personalizado
                </button>
              </div>
            )}
          {buildGroups().map((g) => (
            <div key={g.key} style={{ marginBottom: "1.4rem" }}>
              <h4 style={{ margin: "0 0 .4rem", color: "#0071ce", fontWeight: 800 }}>
                {g.itemName} {g.alias ? `(${g.alias})` : ""}
              </h4>
              <table className={TutorFormStyle.table}>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Talle (ancho x alto)</th>
                    <th>
                      Apodo
                      <button
                        type="button"
                        className={TutorFormStyle.infoButton}
                        onClick={nicknameTooltipText}
                      >
                        <i
                          className="bi bi-question-circle"
                          style={{ color: "white" }}
                        ></i>
                      </button>
                    </th>
                    <th>
                      Personalizado
                      <button
                        type="button"
                        className={TutorFormStyle.infoButton}
                        onClick={initialsTooltipText}
                      >
                        <i
                          className="bi bi-question-circle"
                          style={{ color: "white" }}
                        ></i>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((row) => (
                    <tr key={row.id}>
                      <td data-label="Nombre">{row.customerName}</td>
                      <td data-label="Talle">
                        <select
                          value={row.size}
                          onChange={(e) =>
                            handleRowChange(row.id, "size", e.target.value)
                          }
                        >
                          <option value="">Seleccionar</option>
                          {row.sizesAvailable
                            .filter((s) => s.code !== "E")
                            .map((s) => (
                              <option key={s.code} value={s.code}>
                                {s.code}{" "}
                                {s?.width && s?.height
                                  ? `(${s.width}x${s.height}) cm`
                                  : ""}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td data-label="Apodo">
                        <input
                          type="text"
                          placeholder="ej. (E20) Nico (E10)"
                          value={row.nickname}
                          onChange={(e) =>
                            handleRowChange(row.id, "nickname", e.target.value)
                          }
                        />
                      </td>
                      <td data-label="Personalizado">
                        <input
                          type="text"
                          placeholder="ej. N.S, HUM, etc."
                          value={row.initials}
                          onChange={(e) =>
                            handleRowChange(row.id, "initials", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {formRows.length > 0 && (
            <div className={TutorFormStyle.buttonFixedContainer}>
              <button
                type="submit"
                className={`${TutorFormStyle.buttonPrimary} ${
                  confirmState === "success" ? TutorFormStyle.buttonSuccess : ""
                }`}
                disabled={
                  confirmState === "loading" || confirmState === "success"
                }
              >
                {confirmState === "idle" && (
                  <>
                    Enviar
                    <i
                      className="bi bi-send-fill"
                      style={{ marginLeft: 8 }}
                    ></i>
                  </>
                )}
                {confirmState === "loading" && (
                  <div className={TutorFormStyle.spinner} />
                )}
                {confirmState === "success" && (
                  <span style={{ fontSize: "1.2rem" }}>✓</span>
                )}
              </button>

              <div className={`${TutorFormStyle.menuWrapper}`}>
                <button
                  type="button"
                  className={TutorFormStyle.exportMenuButton}
                  onClick={() => setShowExportMenu(!showExportMenu)}
                >
                  <i className="bi bi-three-dots-vertical"></i>
                </button>
                {showExportMenu && (
                  <ul className={TutorFormStyle.exportMenu}>
                    <li>
                      {" "}
                      {missingSizesData && (
                        <Exporter
                          missingSizesData={missingSizesData}
                          orderData={data}
                        />
                      )}
                    </li>
                  </ul>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {isPreviewing && (
        <>
          <h4>Confirma que los siguientes datos son correctos?</h4>
          <table
            className={TutorFormStyle.table}
            style={{ border: "1px solid #ddd" }}
          >
            <thead>
              <tr>
                <th>Alumno</th>
                <th>Artículo</th>
                <th>Talle</th>
                <th>Apodo</th>
                <th>Personalizado</th>
              </tr>
            </thead>
            <tbody>
              {previewData.map((row, index) => (
                <tr key={index}>
                  <td data-label="Alumno">{row.name}</td>
                  <td data-label="Artículo">
                    {row.itemName} {row?.alias && `(${row?.alias})`}
                  </td>
                  <td data-label="Talle">{row.size}</td>
                  <td data-label="Apodo">{row.nickname}</td>
                  <td data-label="Personalizado">{row.initials}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={TutorFormStyle.buttonFixedContainer}>
            <button
              onClick={goBackToForm}
              type="button"
              className={TutorFormStyle.buttonSecondary}
              disabled={
                confirmState === "loading" || confirmState === "success"
              }
            >
              Volver
            </button>

            <button
              onClick={confirmSubmit}
              type="button"
              className={`${TutorFormStyle.buttonPrimary} ${
                confirmState === "success" ? TutorFormStyle.buttonSuccess : ""
              }`}
              disabled={
                confirmState === "loading" || confirmState === "success"
              }
            >
              {confirmState === "idle" && "Confirmar"}
              {confirmState === "loading" && (
                <div className={TutorFormStyle.spinner} />
              )}
              {confirmState === "success" && (
                <span style={{ fontSize: "1.2rem" }}>✓</span>
              )}
            </button>
          </div>
        </>
      )}
    </form>
  );
};

export default TutorForm;